import { RefreshCw, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@app/components/ui/button";
import dayjs from "dayjs";
import useUser from "@app/zustand/user";
import { Trans } from "@lingui/macro";
import API from "@app/services/api";
import { useState, useCallback, useMemo } from "react";
import { useIsAdmin } from "@app/utils/use-is-admin";

interface SourceConnectionInfo {
  id: string;
  tenant_id: string;
  url: string;
  has_credentials: boolean;
  last_synced_at?: string | null;
}

export function LastSourceSynchro() {
  const user = useUser((state) => state.user);
  const setUser = useUser((state) => state.setUser);
  const isAdmin = useIsAdmin();
  const [isSyncing, setIsSyncing] = useState(false);

  const connections: SourceConnectionInfo[] = useMemo(() => {
    const raw = (user as Record<string, unknown> | null)
      ?.source_connections as SourceConnectionInfo[] | undefined;
    return raw ?? [];
  }, [user]);

  const hasConnections = connections.some((c) => c.has_credentials);

  const lastRefresh = useMemo(() => {
    const dates = connections
      .map((c) => c.last_synced_at)
      .filter(Boolean) as string[];
    if (dates.length === 0) return null;
    return dates.sort().pop() ?? null;
  }, [connections]);

  const handleSync = useCallback(async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const res = await API.post({ path: "/patient/sync-source" });
      if (res?.data?.last_synced_at && user) {
        const updatedConnections = connections.map((c) => ({
          ...c,
          last_synced_at: res.data.last_synced_at,
        }));
        setUser({
          ...user,
          source_connections: updatedConnections,
        } as typeof user);
      }
    } catch {
      // Silently handle — user sees the date didn't change
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, user, setUser, connections]);

  if (connections.length === 0) return null;
  if (!hasConnections) return null;

  const lastRefreshFormatted = lastRefresh
    ? dayjs(lastRefresh).format("DD/MM/YYYY HH:mm")
    : null;

  if (!isAdmin) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        {lastRefresh ? (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span>
              <Trans>Synchro : {lastRefreshFormatted}</Trans>
            </span>
          </>
        ) : (
          <>
            <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span>
              <Trans>Pas encore synchronisé</Trans>
            </span>
          </>
        )}
      </div>
    );
  }

  const syncTitle = lastRefresh
    ? `Dernière synchro : ${lastRefreshFormatted}`
    : "Pas encore synchronisé";

  return (
    <div className="flex items-center gap-2">
      {isSyncing ? (
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm text-slate-500 italic">
            <Trans>Synchronisation en cours...</Trans>
          </span>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          title={syncTitle}
          className="h-7 gap-1 text-xs text-primary border-primary/30 hover:bg-primary/5 hover:border-primary/50"
        >
          <RefreshCw className="h-3 w-3" />
          <Trans>Synchroniser</Trans>
        </Button>
      )}
    </div>
  );
}
